/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, ShieldAlert, RefreshCw, X, AlertCircle, CheckCircle2, 
  Clock, Mail, School, ArrowRight, Check, Search, Filter
} from 'lucide-react';
import { useInAppDialog } from '../Common/InAppDialog';

interface ReconciliationCandidate {
  studentId: string;
  studentName: string;
  studentEmail: string;
  school: string;
  district: string;
  tenantId: string;
  ferpaConsentSentAt: string | null;
  ferpaConsentRecipientEmail: string | null;
  parentEmail: string | null;
  parentName: string;
  canAutoReconcile: boolean;
  detectedSource: string;
  matchedParentEmail: string | null;
  parentGranted: boolean;
  hasAuditLogMatch: boolean;
  suggestedConsentDate: string;
}

interface ReconciliationStatusResponse {
  totalStudents: number;
  grantedCount: number;
  pendingCount: number;
  sentCount: number;
  reconcilableCount: number;
  candidates: ReconciliationCandidate[];
}

interface FerpaReconciliationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReconcileSuccess: () => void;
}

export const FerpaReconciliationModal: React.FC<FerpaReconciliationModalProps> = ({
  isOpen,
  onClose,
  onReconcileSuccess
}) => {
  const { showToast } = useInAppDialog();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReconciliationStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'reconcilable' | 'sent'>('all');
  const [processingIds, setProcessingIds] = useState<Record<string, boolean>>({});
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/ferpa-reconciliation-status');
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok || !contentType.includes('application/json')) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to fetch FERPA reconciliation status.');
      }
      const json: ReconciliationStatusResponse = await res.json();
      setData(json);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Error checking Firestore FERPA records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCorrectStudent = async (studentId: string) => {
    setProcessingIds(prev => ({ ...prev, [studentId]: true }));
    try {
      const res = await fetch('/api/reconcile-ferpa-consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentIds: [studentId] })
      });
      const resData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resData.error || 'Failed to correct student record.');

      showToast({
        title: 'Student Record Corrected',
        message: 'Successfully updated FERPA consent in Firestore.',
        type: 'success'
      });

      await fetchStatus();
      onReconcileSuccess();
    } catch (err: any) {
      showToast({
        title: 'Correction Failed',
        message: err.message || 'Error updating Firestore.',
        type: 'danger'
      });
    } finally {
      setProcessingIds(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const handleReconcileAllMatched = async () => {
    setBulkProcessing(true);
    try {
      const res = await fetch('/api/reconcile-ferpa-consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoFixAll: true })
      });
      const resData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resData.error || 'Failed to reconcile records.');

      showToast({
        title: 'FERPA Consents Reconciled',
        message: `Successfully synchronized ${resData.count} student consent records in Firestore.`,
        type: 'success'
      });

      await fetchStatus();
      onReconcileSuccess();
    } catch (err: any) {
      showToast({
        title: 'Reconciliation Failed',
        message: err.message || 'Error updating Firestore.',
        type: 'danger'
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApproveAllSent = async () => {
    setBulkProcessing(true);
    try {
      const res = await fetch('/api/reconcile-ferpa-consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrideAllSent: true })
      });
      const resData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(resData.error || 'Failed to update records.');

      showToast({
        title: 'Records Synchronized',
        message: `Updated ${resData.count} students with sent FERPA requests in Firestore.`,
        type: 'success'
      });

      await fetchStatus();
      onReconcileSuccess();
    } catch (err: any) {
      showToast({
        title: 'Operation Failed',
        message: err.message || 'Error updating Firestore.',
        type: 'danger'
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const filteredCandidates = (data?.candidates || []).filter(c => {
    if (filterType === 'reconcilable' && !c.canAutoReconcile) return false;
    if (filterType === 'sent' && !c.ferpaConsentSentAt && !c.ferpaConsentRecipientEmail) return false;
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      c.studentName.toLowerCase().includes(q) ||
      c.studentEmail.toLowerCase().includes(q) ||
      c.school.toLowerCase().includes(q) ||
      (c.parentEmail && c.parentEmail.toLowerCase().includes(q)) ||
      c.detectedSource.toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-black uppercase text-[#011d3c] tracking-tight">
                FERPA Consent Firestore Reconciliation Tool
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Audit students whose parents filled out consent or where FERPA consent was sent, and correct Firestore entries.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Area */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Summary Metric Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">Total Students</span>
              <span className="text-xl font-black text-slate-800">{data?.totalStudents ?? '-'}</span>
            </div>
            <div className="p-3 bg-emerald-50/70 rounded-2xl border border-emerald-200/80">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">FERPA Granted</span>
              <span className="text-xl font-black text-emerald-800">{data?.grantedCount ?? '-'}</span>
            </div>
            <div className="p-3 bg-amber-50/70 rounded-2xl border border-amber-200/80">
              <span className="text-[10px] font-black uppercase tracking-wider text-amber-700 block">Consent Sent / Pending</span>
              <span className="text-xl font-black text-amber-800">{data?.sentCount ?? '-'}</span>
            </div>
            <div className="p-3 bg-[#0094c8]/10 rounded-2xl border border-[#0094c8]/25">
              <span className="text-[10px] font-black uppercase tracking-wider text-[#0094c8] block">Matched & Ready to Fix</span>
              <span className="text-xl font-black text-[#011d3c]">{data?.reconcilableCount ?? '-'}</span>
            </div>
          </div>

          {error && (
            <div className="flex items-center space-x-2 p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Search & Filter Toolbar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search candidates by name, email, school..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#0094c8]"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex items-center bg-slate-100 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    filterType === 'all' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All Pending ({data?.candidates?.length ?? 0})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterType('reconcilable')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    filterType === 'reconcilable' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Matched ({data?.reconcilableCount ?? 0})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterType('sent')}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    filterType === 'sent' ? 'bg-white text-slate-800 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Consent Sent ({data?.sentCount ?? 0})
                </button>
              </div>

              <button
                type="button"
                onClick={fetchStatus}
                disabled={loading}
                className="p-2 text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all cursor-pointer"
                title="Refresh scan"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Candidates Table */}
          <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-2xs">
            <div className="max-h-[380px] overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50/90 sticky top-0 z-10 border-b border-slate-200">
                  <tr className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                    <th className="py-3 px-4">Student</th>
                    <th className="py-3 px-4">High School</th>
                    <th className="py-3 px-4">Parent Email & Sent Status</th>
                    <th className="py-3 px-4">Firestore Audit Match</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {loading && !data ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-400">
                        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-[#0094c8]" />
                        <span className="font-bold text-xs uppercase tracking-wider">Scanning Firestore collections...</span>
                      </td>
                    </tr>
                  ) : filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-400 font-bold uppercase tracking-wider text-xs">
                        No pending or missed FERPA consent records match this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((c) => {
                      const isProcessing = !!processingIds[c.studentId];
                      return (
                        <tr key={c.studentId} className="hover:bg-slate-50/70 transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-black text-slate-900">{c.studentName}</div>
                            <div className="text-[10px] text-slate-400 truncate">{c.studentEmail || c.studentId}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-bold text-slate-700">{c.school}</div>
                            {c.district && <div className="text-[9px] text-slate-400 uppercase">{c.district}</div>}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-1.5">
                              <Mail className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-bold text-slate-700">{c.ferpaConsentRecipientEmail || c.parentEmail || 'Not Recorded'}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">
                              {c.ferpaConsentSentAt ? (
                                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                                  <Clock className="w-3 h-3 text-amber-500" />
                                  Sent: {new Date(c.ferpaConsentSentAt).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-slate-400">Consent request not sent</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-tight border ${
                              c.canAutoReconcile
                                ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }`}>
                              {c.canAutoReconcile ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Clock className="w-3 h-3 text-slate-400" />}
                              <span>{c.detectedSource}</span>
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              type="button"
                              disabled={isProcessing || bulkProcessing}
                              onClick={() => handleCorrectStudent(c.studentId)}
                              className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer inline-flex items-center space-x-1 shadow-2xs ${
                                c.canAutoReconcile 
                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                  : 'bg-[#0094c8] hover:bg-[#007ba6] text-white'
                              }`}
                            >
                              {isProcessing ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )}
                              <span>Correct in Firestore</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-5 border-t border-slate-100 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500 font-medium">
            Synchronizes student documents in Firestore and writes an audit log in the <code className="text-slate-700 font-mono">logs</code> collection.
          </div>

          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-all uppercase cursor-pointer"
            >
              Close
            </button>

            {data && data.reconcilableCount > 0 && (
              <button
                type="button"
                disabled={bulkProcessing || loading}
                onClick={handleReconcileAllMatched}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center space-x-2 transition-all cursor-pointer shadow-sm"
              >
                {bulkProcessing ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ShieldCheck className="w-3.5 h-3.5" />
                )}
                <span>Reconcile All Matched ({data.reconcilableCount})</span>
              </button>
            )}

            {data && data.sentCount > 0 && (
              <button
                type="button"
                disabled={bulkProcessing || loading}
                onClick={handleApproveAllSent}
                title="Approve and correct all students where consent was sent"
                className="bg-[#0094c8] hover:bg-[#007ba6] disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center space-x-2 transition-all cursor-pointer shadow-sm"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Synchronize All Sent Records</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
